import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useMatch, useNavigate } from "react-router-dom";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Lazy-loaded page components — split into separate chunks, loaded on demand
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ChatWidget = lazy(() => import("./components/widget/ChatWidget"));
const ResellerChatWidget = lazy(() => import("./components/widget/ResellerChatWidget"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Login = lazy(() => import("./pages/Login"));
const CommandAuth = lazy(() => import("./pages/command/Auth"));
const ResellerLogin = lazy(() => import("./pages/reseller/Login"));
const ResellerDashboard = lazy(() => import("./pages/reseller/Dashboard"));
const ClientConfig = lazy(() => import("./pages/reseller/ClientConfig"));
const CustomServices = lazy(() => import("./pages/reseller/CustomServices"));
const TestOmniVerge = lazy(() => import("./pages/TestOmniVerge"));
const ClientPortalDashboard = lazy(() => import("./pages/portal/Dashboard"));
const WidgetSettings = lazy(() => import("./pages/portal/WidgetSettings"));
const Analytics = lazy(() => import("./pages/portal/Analytics"));
const Support = lazy(() => import("./pages/portal/Support"));
const ClientDashboard = lazy(() => import("./pages/portal/ClientDashboard"));
const VIPCustomerConsole = lazy(() => import("./components/widget/VIPCustomerConsole"));
const ClientAuth = lazy(() => import("./pages/portal/ClientAuth"));

const queryClient = new QueryClient();

// ✅ PUBLIC PATHS - Never guard these routes
const PUBLIC_PATHS = ['/', '/login', '/reseller/login', '/command/auth', '/test-omniverge', '/dashboard', '/vip'];

// ✅ Widget Manager - conditionally renders widgets based on route
// All hooks are called unconditionally to comply with React Rules of Hooks
const WidgetManager = () => {
  const isTestOmniVerge = useMatch("/test-omniverge");

  // Determine visibility based on current path
  const currentPath = window.location.pathname;
  const isResellerPath = currentPath.startsWith('/reseller');
  const isLoginPath = currentPath === '/login' || currentPath === '/command/auth';
  const isAdminPath = currentPath.startsWith('/admin');

  // Kill-switch: no widgets on reseller, login, or admin paths
  if (isResellerPath || isLoginPath || isAdminPath) {
    return null;
  }

  return (
    <>
      {!isTestOmniVerge && <ResellerChatWidget />}
      {isTestOmniVerge && <ChatWidget />}
    </>
  );
};

// ✅ Route Guard - ONLY guards protected routes and enforces domain isolation
// Does NOT handle post-login routing (login pages handle their own redirects)
const RouteGuard = () => {
  const navigate = useNavigate();
  const [guardState, setGuardState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const runGuard = async () => {
      const currentPath = window.location.pathname;
      
      // ✅ PUBLIC PATH BYPASS - Instant, no async, no overlay
      if (PUBLIC_PATHS.includes(currentPath)) {
        if (isMounted) setGuardState('ready');
        return;
      }

      try {
        // ✅ Wait for Supabase session handshake
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (sessionError) {
          setErrorMessage(`Session error: ${sessionError.message}`);
          setGuardState('error');
          return;
        }

        // ✅ PATH-AWARE REDIRECT for unauthenticated users
        if (!session?.user) {
          if (currentPath.startsWith('/reseller')) {
            navigate('/reseller/login', { replace: true });
          } else if (currentPath.startsWith('/command')) {
            navigate('/command/auth', { replace: true });
          } else if (currentPath.startsWith('/portal')) {
            navigate('/login', { replace: true });
          } else {
            navigate('/login', { replace: true });
          }
          setGuardState('ready');
          return;
        }

        // ✅ Fetch user role for domain isolation
        interface UserRecord {
          role: string;
          tenant_id: string;
        }
        const result = await supabase
          .from('users')
          .select('role, tenant_id')
          .eq('id', session.user.id)
          .maybeSingle();
        const userData: UserRecord | null = result.data as UserRecord | null;
        const userError = result.error;

        if (!isMounted) return;

        if (userError) {
          setErrorMessage(`User lookup failed: ${userError.message}`);
          setGuardState('error');
          return;
        }

        // ✅ No user record: show error, don't silently redirect
        if (!userData) {
          setErrorMessage('No user profile found. Contact your administrator.');
          toast.error('Account not found. Please contact support.');
          setGuardState('error');
          return;
        }

        // ✅ DOMAIN ISOLATION: Reseller/Admin on client portal → bounce to reseller dashboard
        // DEVELOPER BYPASS: Allow developer to access portal for testing excellence
        if (['reseller', 'admin', 'support'].includes(userData.role) && userData.role !== 'developer') {
          if (currentPath.startsWith('/portal/') || currentPath.startsWith('/client/')) {
            navigate('/reseller/dashboard', { replace: true });
            setGuardState('ready');
            return;
          }
        }

        // ✅ DOMAIN ISOLATION: Client on reseller console → bounce to their portal
        if (userData.role === 'client') {
          if (currentPath.startsWith('/reseller/') && currentPath !== '/reseller/login') {
            navigate(`/portal/${userData.tenant_id}/settings`, { replace: true });
            setGuardState('ready');
            return;
          }
        }

        setGuardState('ready');
      } catch (err: unknown) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          setErrorMessage(`Guard error: ${errorMessage}`);
          setGuardState('error');
        }
      }
    };

    // Run on initial mount
    runGuard();

    // ✅ Listen ONLY for sign-out to re-run guard (login pages handle sign-in routing)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!isMounted) return;
      if (event === 'SIGNED_OUT') {
        setGuardState('loading');
        runGuard();
      }
      // TOKEN_REFRESHED is safe to ignore - session is still valid
      // SIGNED_IN is handled by the login pages themselves - NO INTERFERENCE
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // ✅ Semi-transparent overlay with error display (never a full lockout)
  if (guardState === 'loading') {
    const currentPath = window.location.pathname;
    if (!PUBLIC_PATHS.includes(currentPath)) {
      return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/80 border border-white/10 rounded-lg p-6 text-center">
            <div className="animate-spin h-6 w-6 border-2 border-white/30 border-t-white rounded-full mx-auto mb-3" />
            <div className="text-white/60 text-sm">Verifying access...</div>
          </div>
        </div>
      );
    }
  }

  // ✅ Error state: visible on page, not hidden in console
  if (guardState === 'error') {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-black/90 border border-red-500/30 rounded-lg p-6 text-center max-w-md">
          <div className="text-red-400 text-lg font-semibold mb-2">Authentication Error</div>
          <div className="text-white/60 text-sm mb-4">{errorMessage}</div>
          <button 
            onClick={() => window.location.href = '/reseller/login'}
            className="px-4 py-2 bg-[#0097b2] text-white rounded-lg text-sm hover:bg-[#00829c] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// ✅ Lightweight Suspense fallback — just a subtle background pulse, no heavy animations
const PageFallback = () => (
  <div className="fixed inset-0 bg-[#060b0d] flex items-center justify-center">
    <div className="animate-pulse h-6 w-6 rounded-full bg-[#0097b2]/30" />
  </div>
);

const AppNew = () => {
  const [showJillConsole, setShowJillConsole] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleGlobalKeyboard = (e: KeyboardEvent) => {
      // SHIFT + J to toggle Jill Console (Admin)
      if (e.shiftKey && e.key === 'J') {
        e.preventDefault();
        localStorage.setItem("luxe-admin-stealth-access", "true");
        setShowJillConsole(prev => !prev);
      }

      // SHIFT + V to navigate to VIP Console route
      if (e.shiftKey && e.key === 'V') {
        e.preventDefault();
        // Get current tenant_id from localStorage or context
        const tenantId = localStorage.getItem('current_tenant_id') || 'dev-admin-001';
        navigate('/vip', { state: { tenantId } });
      }
    };

    window.addEventListener("keydown", handleGlobalKeyboard);
    return () => window.removeEventListener("keydown", handleGlobalKeyboard);
  }, [navigate]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Master Canvas - Physical Background Layer */}
      <div
        id="master-canvas"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: -1,
          backgroundImage: 'url("/images/bg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#060b0d',
          opacity: 1,
          pointerEvents: 'none'
        }}
      />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        
        {/* Stealth Admin Overlay */}
        {showJillConsole && (
          <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
            <div className="p-4 bg-black text-white flex justify-between items-center">
              <span>💎 LUXE STEALTH CONSOLE ACTIVE</span>
              <button 
                onClick={() => setShowJillConsole(false)}
                className="bg-red-900 px-3 py-1 rounded text-xs"
              >
                CLOSE [ESC]
              </button>
            </div>
            <AdminDashboard />
          </div>
        )}

        <>
          <RouteGuard />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Client Portal Routes - Moved to top for testing */}
              <Route path="/dashboard" element={<ClientDashboard />} />
              <Route path="/" element={<Index />} />
              <Route path="/luxe-console" element={<AdminDashboard />} />
              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/command/auth" element={<CommandAuth />} />
              <Route path="/portal/auth" element={<ClientAuth />} />
              <Route path="/reseller/login" element={<ResellerLogin />} />
              {/* Reseller Console Routes */}
              <Route path="/reseller/dashboard" element={<ResellerDashboard />} />
              <Route path="/reseller/client/:tenantId" element={<ClientConfig />} />
              <Route path="/reseller/client/:tenantId/services" element={<CustomServices />} />
              {/* Test page for OmniVerge Global widget */}
              <Route path="/test-omniverge" element={<TestOmniVerge />} />
              <Route path="/portal/dashboard" element={<ClientDashboard />} />
              <Route path="/portal/:clientId" element={<ClientPortalDashboard />}>
                <Route index element={<WidgetSettings />} />
                <Route path="settings" element={<WidgetSettings />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="support" element={<Support />} />
              </Route>
              <Route path="/vip" element={<VIPCustomerConsole />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <WidgetManager />
        </>
        
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default AppNew;